import React, { useCallback, useMemo, useState } from 'react';
import { observer } from '@legendapp/state/react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  TextInput,
  StyleSheet,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import * as FileSystem from 'expo-file-system/legacy';
import { v4 as uuidv4 } from 'uuid';
import theme from '@/config/theme';
import { globalState } from '@/state/globalState';
import { hasDeveloperBypass } from '@/utils/subscriptionAccess';
import { getLogger } from '@/utils/logger';
import { APP_CONSTANTS } from '@/config/constants';
import { databaseService } from '@/services/databaseService';
import { ensureDirectoryExists } from '@/utils/file/fileHelpers';
import { generateTestJpegBase64 } from '@/utils/file/sampleImageGenerator';
import {
  getSettingsContentPaddingBottom,
  getSettingsHeaderPaddingTop,
} from '@/utils/safeArea';

const logger = getLogger('DEV-TEST-LOG-BUFFER');
const { TABLES } = APP_CONSTANTS.DATABASE;

const ActionButton = ({
  label,
  onPress,
  disabled,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
}) => (
  <TouchableOpacity
    style={[styles.actionButton, disabled && styles.actionButtonDisabled]}
    onPress={disabled ? undefined : onPress}
    activeOpacity={disabled ? 1 : 0.8}
  >
    <Text style={styles.actionButtonLabel}>{label}</Text>
  </TouchableOpacity>
);

const DevLogBufferTestScreen = observer(() => {
  const router = useRouter();
  const safeAreaInsets = useSafeAreaInsets();
  const headerPaddingTop = getSettingsHeaderPaddingTop(safeAreaInsets.top);
  const scrollViewBottomPadding = getSettingsContentPaddingBottom(safeAreaInsets.bottom);
  const profile = globalState.user.profile.get();
  const company = globalState.company.data.get();
  const isDeveloper = hasDeveloperBypass(profile?.user_type);
  const defaultEmpresaId =
    company?.id || profile?.empresa_id || '049cf689-84f1-46f5-9460-26def21dce59';

  const [empresaId, setEmpresaId] = useState(defaultEmpresaId);
  const [checklistId, setChecklistId] = useState(uuidv4());
  const [campoId, setCampoId] = useState('b00709a4-8a08-4f38-894d-6f23fdb4b6f5');

  const [localUri, setLocalUri] = useState<string | null>(null);
  const [relativePath, setRelativePath] = useState<string | null>(null);
  const [lastBulkResult, setLastBulkResult] = useState<string | null>(null);
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = useCallback((message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [`[${timestamp}] ${message}`, ...prev].slice(0, 200));
  }, []);

  const ensureChecklistExists = useCallback(async () => {
    if (!empresaId) {
      addLog('Informe uma empresa antes de gerar o checklist fake.');
      return;
    }
    const checklistTable = TABLES.GUINCHO_CHECKLISTS.NAME;
    const existing = await databaseService.readItem(checklistTable, checklistId);
    if (existing) {
      addLog(`Checklist ${checklistId} já existe localmente.`);
      return;
    }
    const now = Date.now();
    await databaseService.createItem(checklistTable, {
      id: checklistId,
      template_id: 'e8e94cd1-364b-4a7b-91ed-5edbdf898af9',
      versao: 1,
      empresa_id: empresaId,
      usuario_id: profile?.id ?? null,
      data_criacao: now,
      status: 'in_progress',
      created_at: now,
      updated_at: now,
    });
    addLog(`Checklist fake ${checklistId} criado.`);
  }, [empresaId, checklistId, profile?.id, addLog]);

  const regenerateChecklistId = useCallback(() => {
    const nextId = uuidv4();
    setChecklistId(nextId);
    setLocalUri(null);
    setRelativePath(null);
    setLastBulkResult(null);
    addLog(`Novo checklist_id gerado: ${nextId}`);
  }, [addLog]);

  const writeLocalImage = useCallback(async () => {
    if (!empresaId) {
      addLog('Informe uma empresa antes de gerar o arquivo.');
      return;
    }
    const base64Payload = generateTestJpegBase64();
    const targetRelative = `checklist-photos/${empresaId}/${checklistId}-${campoId}-${Date.now()}-log-buffer.jpg`;

    if (Platform.OS === 'web') {
      try {
        await databaseService.deleteItem(TABLES.CHECKLIST_PHOTOS.NAME, targetRelative, true);
      } catch {
        // ignora exclusão se o registro não existir
      }
      await databaseService.createItem(
        TABLES.CHECKLIST_PHOTOS.NAME,
        {
          id: targetRelative,
          base64: base64Payload,
          created_at: Date.now(),
          updated_at: Date.now(),
        },
        true
      );
      setLocalUri(targetRelative);
    } else {
      const fileUri = `${FileSystem.documentDirectory}${targetRelative}`;
      const directory = fileUri.substring(0, fileUri.lastIndexOf('/') + 1);
      await ensureDirectoryExists(directory);
      await FileSystem.writeAsStringAsync(fileUri, base64Payload, {
        encoding: FileSystem.EncodingType.Base64,
      });
      setLocalUri(fileUri);
    }

    setRelativePath(targetRelative);
    addLog(
      `Arquivo fake salvo em ${Platform.OS === 'web' ? 'IndexedDB' : 'FileSystem'} (${targetRelative}).`
    );
  }, [empresaId, checklistId, campoId, addLog]);

  const triggerBulkUpsert = useCallback(async () => {
    if (!localUri) {
      addLog('Gere o arquivo local antes de forçar o bulkUpsert.');
      return;
    }
    if (!empresaId) {
      addLog('Informe uma empresa válida antes de enviar.');
      return;
    }

    try {
      await databaseService.bulkUpsertChecklistValues(empresaId, checklistId, {
        [campoId]: localUri,
      });
      setLastBulkResult(`Enviado às ${new Date().toLocaleTimeString()}`);
      addLog(
        'bulkUpsertChecklistValues concluído. Se o buffer estiver ativo, o alerta deve ter sido enviado.'
      );
    } catch (error: any) {
      const message = error?.message || 'Falha ao executar bulkUpsert.';
      setLastBulkResult(`Erro: ${message}`);
      addLog(`Erro ao executar bulkUpsert: ${message}`);
      logger.error('[DEV-TEST-LOG-BUFFER] bulkUpsert falhou', error);
    }
  }, [empresaId, checklistId, campoId, localUri, addLog]);

  const developerOnly = useMemo(() => {
    if (isDeveloper) return null;
    return (
      <View style={styles.restrictedContainer}>
        <Text style={styles.restrictedText}>
          Apenas desenvolvedores têm acesso a este laboratório.
        </Text>
        <TouchableOpacity style={styles.backButtonAlt} onPress={() => router.back()}>
          <MaterialCommunityIcons name="arrow-left" size={20} color="#fff" />
          <Text style={styles.backButtonAltText}>Voltar</Text>
        </TouchableOpacity>
      </View>
    );
  }, [isDeveloper, router]);

  if (!isDeveloper) {
    return developerOnly;
  }

  return (
    <View style={styles.container}>
      <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
        <TouchableOpacity style={styles.backButton} onPress={() => router.back()}>
          <MaterialCommunityIcons name="arrow-left" size={24} color={theme.colors.text.inverse} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Teste buffer de logs</Text>
        <View style={styles.headerRight} />
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.scrollContent, { paddingBottom: scrollViewBottomPadding }]}
        showsVerticalScrollIndicator={false}
      >
        <Text style={styles.helperText}>
          Este laboratório simula o fluxo que salva um caminho local em um checklist e dispara o
          buffer circular. Certifique-se de habilitar o LOG_BUFFER nas constantes antes de iniciar.
        </Text>

        <View style={styles.fieldGroup}>
          <Text style={styles.label}>Empresa</Text>
          <TextInput
            style={styles.input}
            value={empresaId}
            placeholder="empresa_id"
            onChangeText={setEmpresaId}
          />
        </View>

        <View style={styles.fieldGroup}>
          <Text style={styles.label}>Checklist</Text>
          <View style={styles.inlineField}>
            <TextInput
              style={[styles.input, { flex: 1 }]}
              value={checklistId}
              placeholder="checklist_id"
              onChangeText={setChecklistId}
              autoCapitalize="none"
            />
            <TouchableOpacity style={styles.inlineButton} onPress={regenerateChecklistId}>
              <MaterialCommunityIcons name="refresh" size={18} color={theme.colors.text.inverse} />
              <Text style={styles.inlineButtonText}>Gerar</Text>
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.fieldGroup}>
          <Text style={styles.label}>Campo</Text>
          <TextInput
            style={styles.input}
            value={campoId}
            placeholder="campo_id"
            onChangeText={setCampoId}
          />
        </View>

        <View style={styles.actionsRow}>
          <ActionButton label="1) Criar checklist fake" onPress={ensureChecklistExists} />
          <ActionButton label="2) Gerar arquivo local" onPress={writeLocalImage} />
        </View>

        <View style={styles.actionsRow}>
          <ActionButton
            label="3) Forçar bulkUpsert"
            onPress={triggerBulkUpsert}
            disabled={!localUri}
          />
        </View>

        <View style={styles.statusCard}>
          <Text style={styles.statusTitle}>Estado atual</Text>
          <Text style={styles.statusItem}>Local URI: {localUri || '—'}</Text>
          <Text style={styles.statusItem}>Path relativo: {relativePath || '—'}</Text>
          <Text style={styles.statusItem}>Último bulkUpsert: {lastBulkResult || '—'}</Text>
        </View>

        <View style={styles.logsCard}>
          <View style={styles.logsHeader}>
            <Text style={styles.sectionTitle}>Logs em memória</Text>
            <TouchableOpacity onPress={() => setLogs([])}>
              <MaterialCommunityIcons
                name="trash-can-outline"
                size={18}
                color={theme.colors.text.secondary}
              />
            </TouchableOpacity>
          </View>
          {logs.length === 0 ? (
            <Text style={styles.helperText}>Nenhum passo executado ainda.</Text>
          ) : (
            logs.map((entry, idx) => (
              <Text key={idx} style={styles.logEntry}>
                {entry}
              </Text>
            ))
          )}
        </View>
      </ScrollView>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    backgroundColor: theme.colors.primary.default,
  },
  backButton: {
    padding: theme.spacing.xs,
  },
  headerTitle: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
  },
  headerRight: {
    width: 32,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    padding: theme.spacing.md,
    gap: theme.spacing.md,
  },
  helperText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
  },
  fieldGroup: {
    gap: 6,
  },
  inlineField: {
    flexDirection: 'row',
    gap: theme.spacing.xs,
    alignItems: 'center',
  },
  inlineButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
    backgroundColor: theme.colors.primary.default,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.sm,
  },
  inlineButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.xs,
  },
  label: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
  },
  input: {
    borderRadius: theme.borderRadius.sm,
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: Platform.OS === 'web' ? theme.spacing.sm : theme.spacing.xs,
    backgroundColor: theme.colors.background.primary,
    color: theme.colors.text.primary,
  },
  actionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.sm,
  },
  actionButton: {
    flexGrow: 1,
    minWidth: 140,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.sm,
    backgroundColor: theme.colors.primary.default,
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionButtonDisabled: {
    backgroundColor: theme.colors.border.default,
  },
  actionButtonLabel: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.xs,
    textAlign: 'center',
  },
  statusCard: {
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.primary,
    padding: theme.spacing.md,
    gap: 6,
  },
  statusTitle: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
    fontWeight: theme.fontWeights.medium as any,
  },
  statusItem: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
  },
  logsCard: {
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.primary,
    padding: theme.spacing.md,
    gap: 8,
  },
  logsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
    fontWeight: theme.fontWeights.medium as any,
  },
  logEntry: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.secondary,
  },
  restrictedContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.background.secondary,
  },
  restrictedText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
    textAlign: 'center',
    marginBottom: theme.spacing.md,
  },
  backButtonAlt: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
    backgroundColor: theme.colors.primary.default,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.sm,
  },
  backButtonAltText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.xs,
  },
});

export default DevLogBufferTestScreen;
